import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { track } from "@/lib/site";

const STARTERS = ["What products do you supply?", "How do I request a quote?", "Do you offer private label?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(Math.random().toString(36).slice(2) + Date.now().toString(36));
  const bodyRef = useRef(null);
  const messagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, streaming]);

  const openChat = () => {
    setOpen(true);
    track("ai_chat_open");
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    const history = messagesRef.current.map((m) => ({ role: m.role, content: m.text }));
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message, history }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + data.delta };
                return copy;
              });
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: data.error };
                return copy;
              });
            }
          } catch { /* ignore partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry — the assistant is unavailable right now. Please contact our export team on WhatsApp." };
        return copy;
      });
    } finally {
      setStreaming(false);
      track("ai_chat_message");
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={openChat}
          data-testid="ai-chat-toggle"
          aria-label="Ask our AI assistant"
          className="fixed bottom-20 left-4 z-50 flex items-center gap-2 rounded-full border border-gold/50 bg-navy px-4 py-3 text-xs font-bold uppercase tracking-wider text-ivory shadow-[0_10px_30px_-10px_rgba(11,25,44,0.6)] transition-transform hover:scale-105 md:bottom-6 md:left-6"
        >
          <Sparkles size={16} className="text-gold-soft" /> Ask Orvella AI
        </button>
      )}

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-20 left-3 z-50 flex h-[26rem] w-[calc(100vw-1.5rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-line bg-white shadow-[0_24px_60px_-20px_rgba(11,25,44,0.45)] md:bottom-6 md:left-6 md:h-[30rem]"
        >
          <div className="flex items-center justify-between bg-navy px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-gold-soft" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-ivory">Orvella Assistant</p>
                <p className="text-[10px] text-ivory/60">Product & sourcing questions</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="ai-chat-close" className="text-ivory/70 hover:text-ivory">
              <X size={16} />
            </button>
          </div>

          <div ref={bodyRef} className="flex-1 space-y-3 overflow-y-auto bg-ivory px-3 py-3" data-testid="ai-chat-body">
            {messages.length === 0 && (
              <div>
                <p className="text-xs leading-relaxed text-mist">
                  Namaste! I can answer questions about our products, quotation process, samples and export coordination.
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {STARTERS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="chip hover:border-gold" data-testid={`ai-starter-${s.slice(0, 10).replace(/\W/g, "")}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <p className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-navy text-ivory" : "border border-line bg-white text-ink"}`}>
                  {m.text || (streaming && i === messages.length - 1 ? <Loader2 size={13} className="inline animate-spin text-gold-dark" /> : "")}
                </p>
              </div>
            ))}
          </div>

          <form
            className="flex items-center gap-2 border-t border-line bg-white px-3 py-2.5"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              className="field !py-2"
              placeholder="Ask about products, MOQs, samples…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="ai-chat-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} className="btn-gold !px-3 !py-2" aria-label="Send message" data-testid="ai-chat-send">
              {streaming ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
